
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { Calendar, Clock, Loader2, Star } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { Subscription } from "@/types/subscription";
import { Client } from "@/types/types";

interface ExtendSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  currentSubscription?: Subscription;
}

export function ExtendSubscriptionDialog({
  open,
  onOpenChange,
  client,
  currentSubscription,
}: ExtendSubscriptionDialogProps) {
  const { t } = useTranslation(["subscriptions", "common", "clients", "payments"]);
  const { toast } = useToast();
  const { extendActiveSubscriptionForUser, isLoading } = useClientSubscriptionStore();
  const isMobile = useIsMobile();
  
  const [daysToAdd, setDaysToAdd] = useState("0");
  const [sessionsToAdd, setSessionsToAdd] = useState("0");
  const [jokersToAdd, setJokersToAdd] = useState("0");

  const handleSubmit = async () => {
    try {
      // Calculate the new expiry date based on days to add
      const days = parseInt(daysToAdd);
      
      // Convert sessions and jokers to add to appropriate types
      const sessions = parseInt(sessionsToAdd);
      const jokers = parseInt(jokersToAdd);
      
      // Build a subscription update object with data from current subscription if available
      const subscriptionData = {
        additionalSessions: sessions,
        additionalDays: days,
        additionalJokers: jokers,
      };
      
      console.log("Extending subscription with data:", subscriptionData);
      
      // Ensure we're passing a valid client ID (string)
      const success = await extendActiveSubscriptionForUser(String(client.id), subscriptionData);
      
      if (success) {
        toast({
          title: t("subscriptionUpdated"),
          description: t("subscriptionUpdatedMessage", { name: `${client.firstName} ${client.lastName}` }),
          duration: 3000,
        });
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast({
        title: t("error", { ns: "common" }),
        description: t("errorExtendingSubscription", { ns: "subscriptions" }),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("extendSubscription")}</DialogTitle>
          <DialogDescription>
            {t("updateDetails", { name: `${client.firstName} ${client.lastName}` })}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className={isMobile ? "h-[60vh] pr-4" : "h-full"}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4" />
                <Label htmlFor="days">{t("extensionDays")}</Label>
              </div>
              <Input
                id="days"
                type="number"
                min="0"
                value={daysToAdd}
                onChange={(e) => setDaysToAdd(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4" />
                <Label htmlFor="sessions">{t("sessions.addAmount", { ns: "clients" })}</Label>
              </div>
              <Input
                id="sessions"
                type="number"
                min="0"
                value={sessionsToAdd}
                onChange={(e) => setSessionsToAdd(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Star className="h-4 w-4" />
                <Label htmlFor="jokers">{t("jokers.addAmount", { ns: "clients" })}</Label>
              </div>
              <Input
                id="jokers"
                type="number"
                min="0"
                value={jokersToAdd}
                onChange={(e) => setJokersToAdd(e.target.value)}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t("cancel", { ns: "common" })}
          </Button>
          <Button 
            type="button" 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('processing', { ns: 'common' })}
              </>
            ) : (
              t("save", { ns: "common" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
